import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { KeyRound, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { API } from '../context/AuthContext';
import { AuthCard, Field, ErrorBox, formatApiError } from '../components/auth/AuthCard';

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();
  const [valid, setValid] = useState(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setValid(false); return; }
    axios.get(`${API}/auth/reset-password/validate`, { params: { token } })
      .then(r => setValid(!!r.data.valid)).catch(() => setValid(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) return setError('ପାସୱାର୍ଡ ଅତି କମରେ ୮ ଅକ୍ଷର ହେବା ଦରକାର / Min 8 characters');
    if (password !== confirm) return setError('ଦୁଇଟି ପାସୱାର୍ଡ ମେଳ ଖାଉନାହିଁ / Passwords do not match');
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <AuthCard title="ନୂଆ ପାସୱାର୍ଡ ସେଟ୍ କରନ୍ତୁ" subtitle="ଏକ ଶକ୍ତିଶାଳୀ ପାସୱାର୍ଡ ବାଛନ୍ତୁ (ଅତି କମରେ ୮ ଅକ୍ଷର)" back="/login">
      {valid === null && <div className="text-center text-sm text-gray-500 py-4" data-testid="reset-checking">ଲିଙ୍କ୍ ଯାଞ୍ଚ ହେଉଛି…</div>}
      {valid === false && (
        <div className="text-center py-3" data-testid="reset-invalid">
          <AlertTriangle size={44} className="mx-auto text-amber-500" />
          <div className="font-bold text-gray-900 mt-3">ଲିଙ୍କ୍ ଅବୈଧ କିମ୍ବା ମିଆଦ ପୂରିଗଲା</div>
          <p className="text-sm text-gray-600 mt-1">Reset ଲିଙ୍କ୍ ୬୦ ମିନିଟ୍ ପାଇଁ ବୈଧ ଏବଂ କେବଳ ଥରେ ବ୍ୟବହାର ହୁଏ।</p>
          <Link to="/forgot-password" className="inline-block mt-4 text-sm font-bold text-emerald-700" data-testid="reset-request-again">ପୁଣି ଅନୁରୋଧ କରନ୍ତୁ</Link>
        </div>
      )}
      {valid && done && (
        <div className="text-center py-3" data-testid="reset-success">
          <CheckCircle2 size={44} className="mx-auto text-emerald-600" />
          <div className="font-bold text-gray-900 mt-3">ପାସୱାର୍ଡ ବଦଳିଗଲା!</div>
          <p className="text-sm text-gray-600 mt-1">ଏବେ ନୂଆ ପାସୱାର୍ଡ ସହ ଲଗଇନ୍ କରନ୍ତୁ। ଲଗଇନ୍ ପୃଷ୍ଠାକୁ ନେଉଛି…</p>
          <Link to="/login" className="inline-block mt-4 text-sm font-bold text-emerald-700" data-testid="reset-go-login">ଲଗଇନ୍</Link>
        </div>
      )}
      {valid && !done && (
        <form onSubmit={submit} className="space-y-3" data-testid="reset-form">
          <Field label="ନୂଆ ପାସୱାର୍ଡ / New password" type="password" required autoComplete="new-password" testId="reset-password-input"
            value={password} onChange={e => setPassword(e.target.value)} placeholder="ଅତି କମରେ ୮ ଅକ୍ଷର" />
          <Field label="ପୁଣି ଲେଖନ୍ତୁ / Confirm" type="password" required autoComplete="new-password" testId="reset-password-confirm-input"
            value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="••••••••" />
          <ErrorBox message={error} testId="reset-error" />
          <Button type="submit" disabled={busy} data-testid="reset-submit-button"
            className="w-full h-12 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold gap-2">
            <KeyRound size={16} /> {busy ? 'ଅପେକ୍ଷା କରନ୍ତୁ…' : 'ପାସୱାର୍ଡ ବଦଳାନ୍ତୁ'}
          </Button>
        </form>
      )}
    </AuthCard>
  );
}
